import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'
import useLogger from 'src/context/LoggerProvider/useLogger'

/**
 * Lazily load an i18n namespace client-side that was intentionally OMITTED from
 * a page's `serverSideTranslations` array to keep the SSR `__NEXT_DATA__`
 * payload under Next's 128 kB "large page data" threshold.
 *
 * `serverSideTranslations` ships the ENTIRE JSON of every listed namespace into
 * `__NEXT_DATA__` — for every visitor, regardless of whether they ever reach
 * the surface that needs it. For namespaces consumed ONLY by a role- or
 * interaction-gated surface (admin panels, modals, drawers), that's pure waste
 * on the common path. This hook moves that cost off the SSR payload and onto
 * the rare moment the gated surface actually mounts.
 *
 * It fetches the static JSON from `/locales/{lng}/{ns}.json` (Next serves
 * `public/locales/*` there — see `next-i18next.config.js` client `localePath`)
 * and registers it on the live i18next instance via `addResourceBundle`, then
 * flips `ready` so the consuming component re-renders with real strings.
 *
 * Use ONLY for gated surfaces where a sub-100ms key-flash before load is
 * acceptable. NEVER for first-paint or SEO-relevant copy — those must stay in
 * the page's `serverSideTranslations` array. See
 * `docs/performance/large-page-data.md`.
 *
 * @param ns       the namespace to load.
 * @param opts.onError  called once when the fetch fails. The hook NEVER flips
 *                 `ready` on failure, so a caller that gates a surface on it
 *                 would otherwise render nothing, forever, with no signal — an
 *                 admin surface failing silently, which the repo forbids. Use
 *                 it to surface the failure and let the user retry.
 * @param enabled  when `false`, the fetch is deferred — nothing is requested
 *                 until it flips `true`. Pass the gated surface's open flag
 *                 (e.g. `modalOpen`) to avoid fetching on every pageview when
 *                 most visitors never open the surface. Defaults to `true`
 *                 (fetch on mount) for the common admin/owner-panel case.
 * @returns `true` once the namespace is available on the i18n instance.
 */
export default function useLazyNamespace(
  ns: string,
  enabled = true,
  opts?: { onError?: (error: unknown) => void }
): boolean {
  const { i18n } = useTranslation()
  const logger = useLogger()
  const lng = i18n.language || 'es'
  // ALWAYS `false` on the first render — never seeded from
  // `i18n.hasResourceBundle`. The SERVER i18next instance resolves any
  // namespace off disk (hydration.md Trigger N), so a seeded value is `true`
  // on SSR and `false` on a fresh client → the gated surface renders on the
  // server only → #418 (Trigger P, 2026-10-08). The effect below flips it on
  // the client, synchronously-after-mount when the bundle is already there.
  const [ready, setReady] = useState(false)
  // Held in a ref so an inline arrow from the caller cannot re-run the
  // effect (and re-fetch) on every render.
  const onErrorRef = useRef(opts?.onError)
  onErrorRef.current = opts?.onError

  useEffect(() => {
    if (!enabled) return
    if (i18n.hasResourceBundle(lng, ns)) {
      setReady(true)
      return
    }

    let cancelled = false
    fetch(`/locales/${lng}/${ns}.json`)
      .then((res) =>
        res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))
      )
      .then((data: Record<string, unknown>) => {
        if (cancelled) return
        i18n.addResourceBundle(lng, ns, data, true, true)
        setReady(true)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        // Non-fatal: the gated surface keeps rendering raw keys. `warn` (not
        // `error`) so a transient locale-fetch miss doesn't trip Datadog alerts.
        logger.warn(
          'Lazy i18n namespace load failed',
          { ns, lng },
          error instanceof Error ? error : undefined
        )
        onErrorRef.current?.(error)
      })

    return () => {
      cancelled = true
    }
  }, [i18n, lng, ns, logger, enabled])

  return ready
}
